import React, {
    Fragment,
    useEffect,
    useRef,
    useState,
} from 'react';
import {
    Avatar,
    Box,
    Divider,
    IconButton,
    LinearProgress,
    ListItem,
    ListItemAvatar,
    ListItemText,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import axios from 'axios';
import { immediateToast } from 'utils/toast';

const fileInputStyle: any = {
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: '0',
    right: '0',
    bottom: '0',
    left: '0',
    opacity: '0',
    cursor: 'pointer',
    zIndex: 10,
};

type UploaderFileType = {
    file: File;
    url: string;
    status: 'selected' | 'onUpload' | 'uploaded';
    src: string;
};

const UploaderTemplate = ({ children }: any): JSX.Element => {
    return (
        <Box
            sx={{
                width: '100%',
                minHeight: { xs: '200px', lg: '250px', xl: '300px' },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 2,
                padding: 2,
                boxSizing: 'border-box',
                position: 'relative',
                overflow: 'hidden',
                border: '2px dashed',
                borderColor: 'primary.light',
                '&:hover': {
                    borderColor: 'primary.dark',
                },
            }}>
            {children}
        </Box>
    );
};

const pickerMimeByExtension: Record<string, string> = {
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.png': 'image/png',
    '.pdf': 'application/pdf',
};

const buildStrictPickerTypes = (allowedExtensions: string[] = []) => {
    const accept: Record<string, string[]> = {};

    allowedExtensions.forEach((extension) => {
        const normalizedExtension = extension.toLowerCase();
        const mime = pickerMimeByExtension[normalizedExtension];
        if (!mime) return;

        if (!accept[mime]) accept[mime] = [];
        if (!accept[mime].includes(normalizedExtension)) {
            accept[mime].push(normalizedExtension);
        }
    });

    return Object.keys(accept).length
        ? [{ description: 'فایل‌های مجاز', accept }]
        : [];
};

const UploaderInput = ({
    onFileSelected,
    allowedFormats,
    allowedExtensions,
    disabled,
    strictFilePicker,
}: any) => {
    const inputRef = useRef<HTMLInputElement | null>(null);

    const handleStrictPicker = async (event: React.MouseEvent<HTMLInputElement>) => {
        if (disabled || !strictFilePicker) return;

        const pickerWindow = window as any;
        if (typeof pickerWindow.showOpenFilePicker !== 'function') return;

        event.preventDefault();
        event.stopPropagation();

        try {
            const [handle] = await pickerWindow.showOpenFilePicker({
                multiple: false,
                excludeAcceptAllOption: true,
                types: buildStrictPickerTypes(allowedExtensions || []),
            });

            const file = await handle?.getFile?.();
            if (file) onFileSelected(file);
        } catch (error: any) {
            if (error?.name !== 'AbortError') {
                immediateToast('error', {
                    message: 'امکان باز کردن انتخاب‌گر فایل وجود ندارد',
                    timeout: 3000,
                });
            }
        }
    };

    return (
        <input
            ref={inputRef}
            type="file"
            disabled={disabled}
            accept={
                (allowedExtensions || []).length
                    ? (allowedExtensions || []).join(',')
                    : (allowedFormats || []).join(',')
            }
            onClick={handleStrictPicker}
            onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) onFileSelected(file);

                if (inputRef.current) {
                    inputRef.current.value = '';
                }
            }}
            style={fileInputStyle}
        />
    );
};

const UploaderField = (props: any): JSX.Element => {
    const [files, updateFiles] = useState<UploaderFileType[]>([]);

    const validateSize = (file: File) => {
        if (props?.maxSize && file.size > props?.maxSize) {
            immediateToast('error', {
                message: `حجم فایل نباید بیشتر از ${Math.round(props?.maxSize / 1024 / 1024)} مگابایت باشد`,
                timeout: 3000,
            });
            return false;
        }
        return true;
    };

    const validateFormat = (file: File) => {
        const extension = file.name.includes('.')
            ? `.${file.name.split('.').pop()?.toLowerCase()}`
            : '';
        const allowedExtensions = (props?.allowedExtensions || []).map(
            (item: string) => item.toLowerCase(),
        );
        const hasAllowedExtension =
            !allowedExtensions.length || allowedExtensions.includes(extension);
        const hasAllowedMime =
            !props?.allowedFormats?.length ||
            !file.type ||
            props.allowedFormats.includes(file.type);

        if (!hasAllowedExtension || !hasAllowedMime) {
            immediateToast('error', {
                message: 'فرمت فایل انتخاب‌شده مجاز نیست',
                timeout: 3000,
            });
            return false;
        }
        return true;
    };

    useEffect(() => {
        if (props.files) {
            updateFiles(
                props.files.map((fileUrl: string) => {
                    return {
                        url: fileUrl,
                        file: new File([], fileUrl.split('/').pop() || ''),
                    } as UploaderFileType;
                }),
            );
        }
    }, [props.files]);

    const handleOnSelect = (file?: File | null) => {
        if (!file) return;

        if (validateFormat(file) && validateSize(file)) {
            const _file: UploaderFileType = {
                file,
                src: URL.createObjectURL(file),
                url: '',
                status: 'selected',
            };

            updateFiles([...files, _file]);
        }
    };

    const onChangeFile = (file?: UploaderFileType) => {
        if (file) {
            const updatedFiles: UploaderFileType[] = files.map((_file) => {
                if (_file.src === file?.src) {
                    return file;
                }

                return _file;
            });

            updateFiles(updatedFiles);

            props.onChangeFiles && props.onChangeFiles(updatedFiles);
        } else {
            props.onChangeFiles && props.onChangeFiles();
        }
    };

    const onDelete = (file: UploaderFileType) => {
        updateFiles(
            files.filter((_file: UploaderFileType) => _file.url !== file.url),
        );
        props.onDelete && props.onDelete(file);
    };

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                position: 'relative',
            }}>
            <UploaderTemplate>
                <UploaderInput
                    onFileSelected={handleOnSelect}
                    allowedFormats={props.allowedFormats}
                    allowedExtensions={props.allowedExtensions}
                    strictFilePicker={props.strictFilePicker}
                    disabled={props.disabled}
                />
                {props.children}
            </UploaderTemplate>

            <Box
                sx={{
                    display: props.listDisable ? 'none' : 'auto',
                }}>
                {files.map((file: UploaderFileType, index: number) => (
                    <Fragment key={file.file.name + '_' + index}>
                        <UploaderItem
                            url={props?.url}
                            onChangeFile={onChangeFile}
                            file={file}
                            onDelete={onDelete}
                        />
                    </Fragment>
                ))}
            </Box>
        </Box>
    );
};

type UploaderItemProps = {
    url: string;
    file: UploaderFileType;
    onDelete: (file: any) => void;
    onChangeFile: (file?: UploaderFileType) => void;
};

const UploaderItem = ({
    onDelete,
    file,
    onChangeFile,
    url,
}: UploaderItemProps) => {
    const [progress, updateProgress] = useState<number>(0);

    const uploadToServer = (file: UploaderFileType) => {
        file.status = 'onUpload';

        onChangeFile(file);

        let formData = new FormData();

        formData.append('file', file.file);
        const token = localStorage.getItem('token');

        axios
            .post(url, formData, {
                headers: {
                    Authorization: 'Bearer ' + token,
                },
                onUploadProgress: (event: any) => {
                    updateProgress(
                        Math.round((event.loaded / event.total) * 100),
                    );
                },
            })
            .then((res: any) => {
                const uploadedUrl =
                    res?.data?.data ?? res?.data?.response?.data ?? '';

                if (!uploadedUrl) {
                    throw new Error('UPLOAD_RESPONSE_URL_MISSING');
                }

                file.url = uploadedUrl;
                file.status = 'uploaded';
                onChangeFile(file);
            })
            .catch((error: any) => {
                onChangeFile();

                immediateToast('error', {
                    message:
                        error?.response?.data?.errors?.file?.[0] ||
                        error?.response?.data?.message ||
                        (error?.message === 'UPLOAD_RESPONSE_URL_MISSING'
                            ? 'پاسخ بارگذاری فایل نامعتبر است'
                            : 'خطای غیرمنتظره رخ داده است'),
                    timeout: 3000,
                });
            });
    };

    useEffect(() => {
        if (file.status === 'selected') {
            uploadToServer(file);
        }
    }, [file, uploadToServer]);

    return (
        <Box
            sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}>
            <ListItem
                alignItems="center"
                secondaryAction={
                    <IconButton
                        edge="end"
                        aria-label="delete"
                        disabled={file.status !== 'uploaded'}
                        onClick={() => onDelete(file)}>
                        <DeleteIcon
                            sx={{
                                color:
                                    file.status !== 'uploaded'
                                        ? ''
                                        : 'warning.main',
                            }}
                        />
                    </IconButton>
                }>
                <ListItemAvatar>
                    <Avatar
                        sx={{
                            backgroundColor: 'background.paper',
                        }}
                        src={file.src || file.url}
                    />
                </ListItemAvatar>

                <ListItemText
                    primaryTypographyProps={{
                        style: {
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        },
                    }}
                    primary={file.file.name}
                />
            </ListItem>

            {progress > 0 && (
                <LinearProgress
                    color={progress < 100 ? 'inherit' : 'success'}
                    variant="determinate"
                    value={progress}
                    sx={{
                        marginBottom: '10px',
                    }}
                />
            )}

            <Divider sx={{ listStyle: 'none' }} component="li" />
        </Box>
    );
};

export { UploaderField };
